import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../kuba.services';
import { FlexiOnboardingService } from './flexi-onboarding.service';
import { FlexiOnboardingStatus, FlexiOnboardingSummary } from './flexi-onboarding.models';

/**
 * History grid of Responsibilities and Duties form records — the CSM's default
 * view of the tab.
 *
 * Each row is one version of the form with its own audit trail and archived
 * PDF. Opening a row re-enters the wizard on that record and saving overwrites
 * it; "New" creates a separate record pre-filled from the newest existing one,
 * so previous versions are never disturbed.
 */
@Component({
  selector: 'flexi-onboarding-list',
  templateUrl: './onboarding-list.component.html',
  styleUrls: ['./onboarding-list.component.scss'],
  standalone: false
})
export class OnboardingListComponent implements OnInit {
  forms: FlexiOnboardingSummary[] = [];
  loading = true;
  loadError: string | null = null;

  constructor(
    private service: FlexiOnboardingService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  get businessId(): number {
    return BaseServices.BusinessId;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.service.listForms(this.businessId).subscribe({
      next: rows => {
        this.forms = rows;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.loadError = err?.error?.detail
          ?? this.translate.instant('FLEXI_ONBOARDING.LIST.LOAD_FAILED');
      }
    });
  }

  /**
   * Re-open / edit a specific record. Saving from the wizard updates this same
   * record — no new row is written.
   */
  open(form: FlexiOnboardingSummary): void {
    this.router.navigate([form.id], { relativeTo: this.route });
  }

  /**
   * Open the wizard on a new, unsaved form.
   *
   * Nothing is written here on purpose — the record is created by the wizard's
   * first save. Creating it up front put a row in this grid the moment the CSM
   * clicked New, even if they immediately navigated away without entering
   * anything. The wizard's draft is pre-filled from the newest existing record
   * (or the default template), with the previous record's identity, audit and
   * state fields stripped.
   */
  createNew(): void {
    this.router.navigate(['new'], { relativeTo: this.route });
  }

  /** The grid shows two states only: everything pre-completion reads as "Open". */
  isCompleted(form: FlexiOnboardingSummary): boolean {
    return form.status === FlexiOnboardingStatus.Completed;
  }

  statusKey(form: FlexiOnboardingSummary): string {
    return this.isCompleted(form)
      ? 'FLEXI_ONBOARDING.STATUS.COMPLETED'
      : 'FLEXI_ONBOARDING.LIST.STATUS_OPEN';
  }

  /** Re-open a published record, plain edit while it is still open. */
  actionKey(form: FlexiOnboardingSummary): string {
    return this.isCompleted(form)
      ? 'FLEXI_ONBOARDING.LIST.REOPEN'
      : 'FLEXI_ONBOARDING.LIST.EDIT';
  }
}
